/* eslint-disable no-unused-vars */
/* eslint-disable react/style-prop-object */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Fieldset,
  Radio,
  Link,
  Icon,
  Sheet,
  DialogNew,
  Table,
  Spinner,
} from '@optimizely/axiom';
import colors from '@optimizely/design-tokens/dist/json/colors.json';

import { roleDisplayNames, getUserName } from '../utils';
import {
  updateUserRole,
  updateTeamRole,
  fetchUserEntityPermission,
  fetchTeamEntityPermission,
} from '../../api/userApi';

const EditPermission = ({
  accountId,
  projectId,
  entityType,
  entityId,
  entityKey,
  user,
  team,
  setSelectedUser,
  setSelectedTeam,
}) => {
  const isUser = !!user;
  const entity = isUser ? user : team;
  const setSelectedEntity = isUser ? setSelectedUser : setSelectedTeam;
  const [role, setRole] = useState(
    entityType === 'flag' 
      ? entity.role
      : (entity.role === 'none' ? 'view' : entity.role)
  );
  
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [environmentPermissionData, setEnvironmentPermissionData] =
    useState(null);
  const [error, setError] = useState(null);

  const title = entityType === 'flag'
    ? 'Flag Role:'
    : entityType === 'environment'
    ? 'Environment Role:'
    : entityType === 'audience'
    ? 'Audience Role:'
    : '';

  const handleRoleChange = async (newRole) => {
    setRole(newRole);
  };

  const onSave = async () => {
    let isSuccess = false;
    setError(null);
    try {
      setIsSaving(true);

      if (isUser) {
        await updateUserRole(
          accountId,
          projectId,
          entityType,
          entityId,
          entityKey,
          user.user_id,
          user.email,
          role,
        );
        user.role = role;
        setSelectedUser((prevUser) => ({ ...prevUser, role }));
      } else {
        await updateTeamRole(
          accountId,
          projectId,
          entityType,
          entityId,
          entityKey,
          team.team_id,
          role,
        );
        team.role = role;
        setSelectedTeam((prevTeam) => ({ ...prevTeam, role }));
      }
      isSuccess = true;
    } catch (err) {
      // Check if the error is from trying to edit own permissions
      // This message is sent by the backend (permission-service).
      if (err.message === 'Cannot change own permissions.') {
        setError(err.message);
      } else {
        setError('Unable to update permissions.');
      }
    } finally {
      setIsSaving(false);
      if (isSuccess) {
        setSelectedEntity(null);
      }
    }
  };

  useEffect(() => {
    const fetchData = async (entityType) => {
      setIsLoading(true);
      setError(null);
      try {
        const entityPermissions = isUser
          ? await fetchUserEntityPermission(
              projectId,
              entityType,
              user.user_id,
            )
          : await fetchTeamEntityPermission(
              projectId,
              entityType,
              team.team_id,
            );

        if (entityType === 'environment') {
          setEnvironmentPermissionData(entityPermissions);
        } else if (entityType === 'audience') {
          // default audience role based on context
          for (const permission of entityPermissions) {
            if (Number(permission.id) === Number(entityId)) {
              setRole(permission.role);
              break;
            }
          }
        }
      } catch (err) {
        setError(
          'Unable to retrieve environment permissions.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (entityType === 'flag') {
      // need environment permission for flag permission dialog
      fetchData('environment');
    } else if (entityType === 'audience') {
      if (!entity.role || entity.role === 'unknown') {
        // current audience role is not known.
        // fetch context-based audience permission
        fetchData(entityType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, entityId, entityType]);

  return (
    setSelectedEntity && (
      <>
        <Sheet
          testSection="permission-update-dialog"
          footerButtonList={[
            <Button
              key={0}
              onClick={() => {
                setSelectedEntity(null);
              }}>
              Cancel
            </Button>,
            <Button key={1} onClick={onSave} style="highlight">
              Save
            </Button>,
          ]}
          onClose={() => setSelectedEntity(null)}
          title="">
          <Fieldset>
            <Link
              href="#"
              onClick={(event) => {
                event.preventDefault();
                setSelectedEntity(null);
              }}>
              <Icon
                name="arrow-left"
                size="small"
                color={colors.brandBlueDark}
              />
              Manage Permissions
            </Link>
          </Fieldset>
          <Fieldset title={isUser ? getUserName(user) : team.name}>
            Learn more about permission management in our help docs. To edit
            project roles, visit the{' '}
            <Link href={`/v2/projects/${projectId}/settings/collaborators`}>
              Admin Center
            </Link>
            .
            {entityType === 'environment' && (
              <>
                {' '}
                To edit Flag permission, visit the{' '}
                <Link href={`/v2/projects/${projectId}/flags/list`}>
                  Flags page
                </Link>
                .
              </>
            )}
          </Fieldset>
          {isSaving ? (
            <div className="width--1-1 flex flex--dead-center">
              <Spinner hasOverlay={true} />
            </div>
          ) : (
            <div>
              <Fieldset title={title}>
                <Table density={'loose'} tableLayoutAlgorithm="auto">
                  <Table.TBody>
                    <Table.TR>
                      <Table.TD>
                        <Radio
                          checked={role === 'admin'}
                          onChange={() => handleRoleChange('admin')}
                          name="Admin"
                          label="Admin"
                          isDisabled={false}
                        />
                      </Table.TD>
                      <Table.TD>
                        <ul className="position--relative" style={{ listStyleType: 'disc' }}>
                          {entityType === 'flag' ? (
                            <>
                              <li>Can modify other users' Flag role.</li>
                              <li>Includes all Editor permissions below.</li>
                            </>
                          ) : entityType === 'environment' ? (
                            <>
                              <li>Can modify other users' Environment role.</li>
                              <li>Includes all Publisher permissions below.</li>
                            </>
                          ) : entityType === 'audience' ? (
                            <>
                              <li>Can modify other users' Audience role.</li>
                              <li>Includes all Editor permissions below.</li>
                            </>
                          ) : null}
                        </ul>
                      </Table.TD>
                    </Table.TR>
                    
                    {entityType === 'environment' && (
                      <Table.TR>
                        <Table.TD>
                          <Radio
                            checked={role === 'publish'}
                            onChange={() => handleRoleChange('publish')}
                            name="Publisher"
                            label="Publisher"
                            isDisabled={false}
                          />
                        </Table.TD>
                        <Table.TD>
                          <ul className="position--relative" style={{ listStyleType: 'disc' }}>
                            <li>Can publish changes to the environment.</li>
                            <li>Includes all Editor permissions below.</li>
                          </ul>
                        </Table.TD>
                      </Table.TR>
                    )}

                    <Table.TR>
                      <Table.TD>
                        <Radio
                          checked={role === 'edit'}
                          onChange={() => handleRoleChange('edit')}
                          label="Editor"
                          name="Editor"
                          isDisabled={false}
                        />
                      </Table.TD>
                      <Table.TD>
                        <ul className="position--relative" style={{ listStyleType: 'disc' }}>
                          {entityType === 'flag' ? (
                            <>
                              <li>Can adjust Flag settings.</li>
                              <li>Can create Variables and Variations.</li>
                              <li>Can create and edit Rules based on Environment role.</li>
                            </>
                          ) : entityType === 'environment' ? (
                            <li>Can edit non-published items in the Environment.</li>
                          ) : entityType === 'audience' ? (
                            <>
                              <li>Can edit the Audience.</li>
                            </>
                          ) : null}
                          <li>Includes all Viewer permissions below.</li>
                        </ul>
                      </Table.TD>
                    </Table.TR>

                    <Table.TR>
                      <Table.TD>
                        <Radio
                          checked={role === 'view'}
                          onChange={() => handleRoleChange('view')}
                          label="Viewer"
                          name="Viewer"
                          isDisabled={false}
                        />
                      </Table.TD>
                      <Table.TD>
                        <ul className="position--relative" style={{ listStyleType: 'disc' }}>
                          {entityType === 'flag' ? (
                            <li>Can access the Flag and view its contents.</li>
                          ) : entityType === 'environment' ? (
                            <li>Can view the Environment and its settings.</li>
                          ) : entityType === 'audience' ? (
                            <li>Can view the Audience and its settings.</li>
                          ) : null}
                        </ul>
                      </Table.TD>
                    </Table.TR>

                    {entityType === 'flag' && (
                      <Table.TR>
                        <Table.TD>
                          <Radio
                            checked={role === 'none'}
                            onChange={() => handleRoleChange('none')}
                            label="None"
                            name="None"
                            isDisabled={false}
                          />
                        </Table.TD>
                        <Table.TD>
                          <ul className="position--relative" style={{ listStyleType: 'disc' }}>
                            <li>Cannot view the Flag or its contents.</li>
                          </ul>
                        </Table.TD>
                      </Table.TR>
                    )}

                    {isLoading && (
                      <Table.TR>
                        <Table.TD colSpan={3}>
                          <div data-testid="loading-spinner">
                            <Spinner hasOverlay={true} />
                          </div>
                        </Table.TD>
                      </Table.TR>
                    )}
                  </Table.TBody>
                </Table>
              </Fieldset>
              {entityType === 'flag' && (
                <>
                  <Fieldset
                    title="Flag + Environment Permissions"
                    titleSize="small">
                    The {isUser ? "user's" : "team's"} ability to modify a Flag
                    in an Environment is determined by whichever permission is
                    lower (Flag or Environment).
                  </Fieldset>
                  <Fieldset title="">
                    <Table density={'loose'} tableLayoutAlgorithm="auto">
                      <Table.THead>
                        <Table.TR>
                          <Table.TH>Environment</Table.TH>
                          <Table.TH>Environment Role</Table.TH>
                          <Table.TH> </Table.TH>
                        </Table.TR>
                      </Table.THead>
                      <Table.TBody>
                        {!isLoading &&
                          environmentPermissionData &&
                          environmentPermissionData.map(
                            (environmentPermission, index) => (
                              <Table.TR key={index}>
                                <Table.TD>
                                  <div>{environmentPermission.name}</div>
                                </Table.TD>
                                <Table.TD>
                                  {roleDisplayNames[
                                    environmentPermission.role
                                  ] || '-'}
                                </Table.TD>
                                <Table.TD></Table.TD>
                              </Table.TR>
                            ),
                          )}
                      </Table.TBody>
                    </Table>
                  </Fieldset>
                  <Fieldset>
                    {'Go to '}
                    <Link
                      testSection="features-dash-intro-dialog-link-to-docs"
                      href={`https://docs.developers.optimizely.com/feature-experimentation/docs/manage-environment-roles`}
                      newWindow={true}
                      key={1}>
                      {'Environments configuration '}
                      <Icon
                        color="color--brand"
                        name="arrow-up-right-from-square"
                        size="small"
                      />
                    </Link>
                  </Fieldset>
                </>
              )}
            </div>
          )}
        </Sheet>
          {error && (
            <DialogNew
              title={error === 'Cannot change own permissions.' 
                ? "Permission denied" 
                : error.includes('retrieve') 
                  ? "Loading issue"
                  : "Update issue"}
              testSection="error-modal-edit-permission"
              hasCloseButton={true}
              onClose={() => {
                setError(null);
                setSelectedEntity(null);
              }}
              footerButtonList={[
                <Button
                  key="close"
                  style="highlight"
                  onClick={() => {
                    setError(null);
                    setSelectedEntity(null);
                  }}>
                  Close
                </Button>,
              ]}>
              <p>{error}</p>
            </DialogNew>
          )}
      </>
    )
  );
};

export default EditPermission;
